
import { Lead } from '@/types/lead';
import { supabase } from '@/integrations/supabase/client';
import { mapDbLeadToLead } from '../mappers/leadMapper';
import { getLeadImages } from '@/services/leadImagesService';
import { getKeyContacts } from '../keyContactService';
import { getLeadsQuery } from './shared/getLeadsQuery';

export const getLeadById = async (id: string): Promise<Lead | null> => {
  try {
    // Get the current user's company ID
    const { data: userData, error: userError } = await supabase.auth.getUser();
    
    // Check for session errors and throw to trigger redirect
    if (userError) {
      console.error('Auth error in getLeadById:', userError);
      throw new Error('Authentication required. Please log in again.');
    }
    
    if (!userData.user) {
      console.warn('No user found in getLeadById');
      return null;
    }
    
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('company_id')
      .eq('id', userData.user.id)
      .maybeSingle();
      
    if (profileError || !profileData?.company_id) {
      console.error('Error fetching user profile or no company set:', profileError);
      return null;
    }
    
    const companyId = profileData.company_id;
    
    // Use the company ID to filter leads
    const { data, error } = await getLeadsQuery(companyId)
      .eq('id', id)
      .maybeSingle();

    if (error) {
      console.error('Error fetching lead by ID:', error);
      throw error;
    }

    if (!data) {
      console.warn(`No lead found with ID: ${id}`);
      return null;
    }

    const lead = mapDbLeadToLead(data);
    
    return {
      ...lead,
      images: (data.lead_images || []).map((img: any) => ({
        id: img.id,
        leadId: id,
        imageUrl: img.image_url,
        isPrimary: img.is_primary,
        position: img.position,
        createdAt: img.created_at
      })),
      keyContacts: (data.lead_key_contacts || []).map((contact: any) => ({
        id: contact.id,
        leadId: id,
        name: contact.name,
        email: contact.email,
        phone: contact.phone,
        position: contact.position,
        isPrimary: contact.is_primary,
        notes: contact.notes,
        createdAt: contact.created_at,
        createdBy: contact.created_by,
        linkedinUrl: contact.linkedin_url || undefined,
        source: contact.source || 'manual',
        status: contact.status || 'active'
      }))
    };
  } catch (error) {
    console.error('Error in getLeadById:', error);
    return null;
  }
};
